import React from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Avatar from "components/Avatar";
import DataTable from "components/DataTable";
import {
	FiCalendar,
	FiCheck,
	FiMessageSquare,
	FiMoreHorizontal,
	FiPaperclip,
	FiX,
} from "react-icons/fi";
import { Dropdown, FormGroup, FormControl } from "react-bootstrap";
import * as employeesActions from "actions/employeesActions";
import * as workflowsActions from "actions/workflowsActions";
import { workflowsService } from "services";
import { wfStatus } from "services/workflowsService";
import { getDiffDays } from "utils/datetime";
import { errorFormating } from "utils/utils";

class PendingAbsences extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			answersById: {},
		};
	}

	async handleAbsenceStatus(absence, isApproved, answer) {
		const { t } = this.props;
		let status = wfStatus.approved;

		if (!isApproved) {
			status = wfStatus.denied;
		} else {
			const currentUser = this.props.user;

			if (
				currentUser &&
				currentUser.groups_supervised &&
				absence.requester &&
				absence.requester.employee_with_group &&
				absence.requester.employee_with_group.group
			) {
				const workflowGroup = absence.requester.employee_with_group.group;

				if (absence.status === wfStatus.pendingSecondLevel) {
					status = wfStatus.approved;
				} else if (
					absence.status === wfStatus.pending &&
					workflowGroup.two_levels_workflow
				) {
					status = wfStatus.pendingSecondLevel;
				}
			}
		}

		const localAnswer =
			answer !== undefined ? answer : this.state.answersById[absence.id];
		if (localAnswer !== undefined) {
			absence.answer_comments = localAnswer;
		}

		absence.shift_id = absence.shift.id;
		absence.status = status;

		const response = await workflowsService.update(absence);

		if (response.ok) {
			toastr.success(t("common.success"), t("common.changesSaved"));

			await this.props.reloadAbsences();
			this.setState((prev) => {
				const next = { ...prev.answersById };
				delete next[absence.id];
				return { answersById: next };
			});
			return true;
		} else {
			const textError = errorFormating(response);
			this.props.openAlertModal(
				textError.general,
				t("absences.errorManaging")
			);
			return false;
		}
	}

	handleChange = (event, absence) => {
		const value = event.target.value;
		this.setState((prev) => ({
			answersById: { ...prev.answersById, [absence.id]: value },
		}));
	};

	getAnswer = (absence) => {
		const localAnswer = this.state.answersById[absence.id];
		return localAnswer !== undefined ? localAnswer : absence.answer_comments || "";
	};

	getPeriodLabel = (absence) => {
		const start = moment(absence.start);
		const end = absence.end ? moment(absence.end) : null;
		const startLabel = start.format("D MMM");

		if (!end || start.isSame(end, "day")) return startLabel;
		return `${startLabel} - ${end.format("D MMM")}`;
	};

	getDurationLabel = (absence) => {
		if (absence.workflow_hourly_requested) {
			return `${String(absence.duration || 0).replace(".", ",")} h`;
		}

		let days = absence.effective_days || 0;
		if (days === 0) days = getDiffDays(absence.start, absence.end);
		return `${days} d`;
	};

	getAbsenceCode = (absence) => {
		const shiftName =
			(absence.shift && absence.shift.name) || absence.shift_type || "";
		const words = shiftName.match(/[\p{L}\p{N}]+/gu) || [];
		return words
			.slice(0, 3)
			.map((word) => word.charAt(0))
			.join("")
			.toUpperCase();
	};

	getTypeColor = (absence) => {
		const shift = absence.shift || {};
		return shift.border_color || shift.background_color || "#5a49ff";
	};

	getTypeBackground = (color) => {
		if (/^#[0-9a-f]{6}$/i.test(color)) return `${color}18`;
		return "#f4f2ff";
	};

	downloadAttachment = async (absence, attachment) => {
		const response = await workflowsService.getAttachment(
			absence.id,
			attachment.url
		);
		const url = window.URL.createObjectURL(response);
		const link = document.createElement("a");
		link.href = url;
		link.download = attachment.name;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);
	};

	renderEmployee = (absence) => {
		const employee = absence.requester;
		return (
			<div className="pending-absence-employee">
				<Avatar
					userId={employee.id}
					className="avatar-sm"
					alt={employee.name}
				/>
				<div>
					<strong>{employee.name} {employee.surname}</strong>
					<span>{employee.position || this.props.t("absences.noPosition")}</span>
				</div>
			</div>
		);
	};

	renderType = (absence, showName = true) => {
		const color = this.getTypeColor(absence);
		const shiftName =
			(absence.shift && absence.shift.name) || absence.shift_type || "";

		return (
			<div className="pending-absence-type">
				<span
					className="pending-absence-code"
					style={{
						borderColor: color,
						backgroundColor: this.getTypeBackground(color),
					}}
				>
					{this.getAbsenceCode(absence)}
				</span>
				{showName && <span className="pending-absence-type-name">{shiftName}</span>}
				{showName && (
					<span className="pending-absence-type-duration">
						· {this.getDurationLabel(absence)}
					</span>
				)}
			</div>
		);
	};

	renderAttachments = (absence, compact = false) => {
		if (!absence.attachments || absence.attachments.length === 0) {
			return compact ? <span>-</span> : null;
		}

		return (
			<div className="pending-absence-attachments">
				{absence.attachments.map((attachment) => (
					<button
						type="button"
						key={attachment.url}
						onClick={() => this.downloadAttachment(absence, attachment)}
						title={attachment.name}
					>
						<FiPaperclip aria-hidden="true" />
						<span>{attachment.name}</span>
						{compact && attachment.size ? (
							<small>({Math.round(attachment.size / 1000)}kb)</small>
						) : null}
					</button>
				))}
			</div>
		);
	};

	renderCard = (absence) => {
		const { t } = this.props;
		return (
			<article className="pending-absence-card" key={absence.id}>
				<header className="pending-absence-card-header">
					{this.renderEmployee(absence)}
					{absence.status === wfStatus.pending && (
						<Dropdown>
							<Dropdown.Toggle variant="action" aria-label={t("common.actions")}>
								<FiMoreHorizontal />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item
									as="button"
									onClick={() => this.props.openEditAbsenceModal(absence)}
								>
									{t("absences.editAction")}
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					)}
				</header>

				<div className="pending-absence-card-body">
					<div className="pending-absence-period">
						<FiCalendar aria-hidden="true" />
						<strong>{this.getPeriodLabel(absence)}</strong>
					</div>
					{this.renderType(absence)}

					<FormGroup
						className="pending-absence-card-comment-field"
						controlId={`answer_comments_${absence.id}`}
					>
						<FormControl
							as="textarea"
							value={this.getAnswer(absence)}
							onChange={(event) => this.handleChange(event, absence)}
							className="pending-absence-comments"
							placeholder={t("absences.pendingView.comments")}
						/>
					</FormGroup>

					<div className="pending-absence-card-buttons">
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => this.handleAbsenceStatus(absence, true)}
						>
							{t("absences.approve")}
						</button>
						<button
							type="button"
							className="btn btn-secondary"
							onClick={() => this.handleAbsenceStatus(absence, false)}
						>
							{t("absences.deny")}
						</button>
					</div>

					<div className="pending-absence-card-requested">
						{t("absences.requestedOn")} {moment(absence.requested).format("DD/MM/YYYY")}
					</div>
					{this.renderAttachments(absence)}
				</div>
			</article>
		);
	};

	renderTableActions = (absence) => {
		const { t } = this.props;

		return (
			<div className="action-row">
					<button
						type="button"
						className="btn-action-icon btn-action-icon-confirm"
						title={t("absences.approve")}
						onClick={() => this.handleAbsenceStatus(absence, true)}
					>
						<FiCheck />
					</button>
					<button
						type="button"
						className="btn-action-icon btn-action-icon-cancel"
						title={t("absences.deny")}
						onClick={() => this.handleAbsenceStatus(absence, false)}
					>
						<FiX />
					</button>
					<button
						type="button"
						className="btn-action-icon btn-action-icon-comment"
						title={t("absences.pendingView.comments")}
						onClick={() =>
							this.props.openAnswerModal(
								absence,
								(isApproved, answer) =>
									this.handleAbsenceStatus(absence, isApproved, answer)
							)
						}
					>
						<FiMessageSquare />
					</button>
			</div>
		);
	};

	renderTable = () => {
		const { t } = this.props;
		const columns = [
			{
				key: "employee",
				header: t("common.employee"),
				className: "pending-absence-data-table-employee",
				sortAccessor: (absence) =>
					`${absence.requester.name} ${absence.requester.surname}`,
				render: (absence) => this.renderEmployee(absence),
			},
			{
				key: "date",
				header: t("absences.pendingView.date"),
				className: "pending-absence-data-table-date",
				sortAccessor: (absence) => moment(absence.start).valueOf(),
				render: (absence) => this.getPeriodLabel(absence),
			},
			{
				key: "type",
				header: t("common.type"),
				className: "pending-absence-data-table-type",
				sortAccessor: (absence) =>
					(absence.shift && absence.shift.name) || absence.shift_type,
				render: (absence) => this.renderType(absence, false),
			},
			{
				key: "requested",
				header: t("absences.requestedOn"),
				className: "pending-absence-data-table-requested",
				sortAccessor: (absence) => moment(absence.requested).valueOf(),
				render: (absence) => moment(absence.requested).format("DD/MM/YYYY"),
			},
			{
				key: "file",
				header: t("absences.pendingView.file"),
				className: "pending-absence-data-table-file",
				sortable: false,
				render: (absence) => this.renderAttachments(absence, true),
			},
		];

		return (
			<DataTable
				className="pending-absence-data-table-wrap"
				tableClassName="table-absences pending-absence-data-table table-hover"
				columns={columns}
				rows={this.props.pending_absences}
				rowKey={(absence) => absence.id}
				emptyMessage={t("absences.noPendingSupervision")}
				actionsColumn={{
					header: t("common.actions"),
					className: "pending-absence-data-table-actions-cell",
					render: (absence) => this.renderTableActions(absence),
				}}
			/>
		);
	};

	render() {
		if (this.props.pending_absences.length === 0) {
			return (
				<p className="no-results">
					{this.props.t("absences.noPendingSupervision")}
				</p>
			);
		}

		return (
			<div className="pending-absences-content">
				{!this.props.is_list_view ? (
					<div className="pending-absence-grid">
						{this.props.pending_absences.map(this.renderCard)}
					</div>
				) : (
					this.renderTable()
				)}
			</div>
		);
	}
}

const mapStateToProps = (reducers) => ({
	...reducers.shiftsReducer,
	user: reducers.authReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
	getEmployeeAbsences: (employee_id) =>
		dispatch(employeesActions.getEmployeeAbsences(employee_id)),
	getManagedAbsences: (options) =>
		dispatch(workflowsActions.getManagedAbsences(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
	withTranslation()(PendingAbsences)
);
